import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type { Alert } from '@/@types/alert';
import { useGroupStore } from './group';
import api from '@/api';
import Common from '@/libs/common';
import { isMockTagsEnabled, mockAlerts, onMockTagsReady } from '@/libs/dev/mock-tags';

export type AlertsState = 'loading' | 'ready' | 'error';

export const useAlertsStore = defineStore('alerts', () => {
  const groupStore = useGroupStore();

  const alerts = ref<Array<Alert>>([]); // = allAlerts();
  const state = ref<AlertsState>('loading');

  let fetchedGroupId: string | undefined;
  // A response for a previous group must not land in the current one.
  let fetchRun = 0;

  const alertCount = computed<number>(() => {
    return alerts.value.length;
  });

  const mocking = import.meta.env.DEV && isMockTagsEnabled();

  function getAlerts(groupId?: string) {
    groupId = groupId || groupStore?.group?.group_id;
    if (!groupId) return;
    if (groupId !== fetchedGroupId) {
      fetchedGroupId = groupId;
      alerts.value = [];
      state.value = 'loading';
    }
    const run = ++fetchRun;
    // On failure the last alerts stay on screen, mock mode shows its own anyway
    const request = api.getAlerts(groupId).catch(() => (mocking ? [] : null));
    request.then(alertsAPI => {
      if (run !== fetchRun) return;
      if (!alertsAPI) {
        state.value = 'error';
        return;
      }
      const alertsArray = [];
      for (const alertId in alertsAPI) {
        const alert = alertsAPI[alertId];
        if (!alert.active) {
          continue;
        }
        if (alert.active_date_times) {
          const now = new Date();
          for (const dateTime of alert.active_date_times) {
            const alertStartDate = Common.dateGtfsFormatToObj(dateTime.start_date);
            const [startHours, startMinutes, startSeconds] = dateTime.start_time
              .split(':')
              .map(string => parseInt(string, 10));
            alertStartDate.setHours(startHours, startMinutes, startSeconds);
            const alertEndDate = Common.dateGtfsFormatToObj(dateTime.end_date);
            const [endHours, endMinutes, endSeconds] = dateTime.end_time
              .split(':')
              .map(string => parseInt(string, 10));
            alertEndDate.setHours(endHours, endMinutes, endSeconds);
            if (now >= alertStartDate && now <= alertEndDate) {
              alertsArray.push(alert);
            }
          }
        } else {
          alertsArray.push(alert);
        }
      }
      alerts.value = mocking ? [...mockAlerts(), ...alertsArray] : alertsArray;
      state.value = 'ready';
    });
  }

  function retry() {
    state.value = 'loading';
    getAlerts();
  }

  // The alerts are fetched before the GTFS, whose routes and stops the mocks target
  if (mocking) onMockTagsReady(() => getAlerts());

  return {
    alerts,
    alertCount,
    state,
    getAlerts,
    retry,
  };
});
